import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  IClientMatchQuery,
  IClientMatchResponse,
  IDialogInboxDetail,
  IDialogInboxImportRequest,
  IDialogInboxProcessRequest,
  IDialogInboxRow,
  IRealEstateMatchQuery,
  IRealEstateMatchResponse,
} from '../../Interfaces/IDialogInbox';
import { customFetcher } from '../useFetch';

const QUERY_KEY = 'dialoginbox';

export const useDialogInboxList = (showAll: boolean) => {
  const {
    data: dialogInboxList,
    isLoading: isLoadingDialogInboxList,
    refetch: refetchDialogInboxList,
  } = useQuery<IDialogInboxRow[]>({
    refetchOnWindowFocus: false,
    queryKey: [QUERY_KEY, { showAll }],
    queryFn: () =>
      customFetcher({
        endpoint: `/dialoginbox${showAll ? '?showAll=true' : ''}`,
      }),
  });

  return { dialogInboxList, isLoadingDialogInboxList, refetchDialogInboxList };
};

export const useDialogInboxDetail = (idDialogInbox: number | null) => {
  const { data: dialogInboxDetail, isLoading: isLoadingDialogInboxDetail } =
    useQuery<IDialogInboxDetail>({
      refetchOnWindowFocus: false,
      enabled: idDialogInbox !== null,
      queryKey: [QUERY_KEY, idDialogInbox],
      queryFn: () =>
        customFetcher({ endpoint: `/dialoginbox/${idDialogInbox}` }),
    });

  return { dialogInboxDetail, isLoadingDialogInboxDetail };
};

export const useSearchClients = () => {
  const { mutateAsync: searchClients, isPending: isSearchingClients } =
    useMutation({
      mutationFn: ({
        idDialogInbox,
        query,
      }: {
        idDialogInbox: number;
        query: IClientMatchQuery;
      }) =>
        customFetcher({
          endpoint: `/dialoginbox/${idDialogInbox}/searchClients`,
          method: 'POST',
          body: query,
        }) as Promise<IClientMatchResponse>,
    });

  return { searchClients, isSearchingClients };
};

export const useSearchRealEstates = () => {
  const { mutateAsync: searchRealEstates, isPending: isSearchingRealEstates } =
    useMutation({
      mutationFn: ({
        idDialogInbox,
        query,
      }: {
        idDialogInbox: number;
        query: IRealEstateMatchQuery;
      }) =>
        customFetcher({
          endpoint: `/dialoginbox/${idDialogInbox}/searchRealEstates`,
          method: 'POST',
          body: query,
        }) as Promise<IRealEstateMatchResponse>,
    });

  return { searchRealEstates, isSearchingRealEstates };
};

export const useProcessDialogInbox = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: processDialogInbox, isPending: isProcessing } =
    useMutation({
      mutationFn: ({
        idDialogInbox,
        body,
      }: {
        idDialogInbox: number;
        body: IDialogInboxProcessRequest;
      }) =>
        customFetcher({
          endpoint: `/dialoginbox/${idDialogInbox}/process`,
          method: 'POST',
          body,
        }) as Promise<{ idLegalTransaction: number }>,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      },
    });

  return { processDialogInbox, isProcessing };
};

export const useImportDialogInbox = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: importDialogInbox, isPending: isImporting } =
    useMutation({
      mutationFn: (body: IDialogInboxImportRequest) =>
        customFetcher({
          endpoint: '/dialoginbox/import',
          method: 'POST',
          body,
        }) as Promise<IDialogInboxDetail>,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      },
    });

  return { importDialogInbox, isImporting };
};

export interface IOpenFormularePullResult {
  status: 'inactive' | 'not_configured' | 'unreachable' | 'ok';
  imported: number;
  failed: number;
  total: number;
  message?: string;
}

export const useOpenFormularePull = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: pullOpenFormulare, isPending: isPullingOpenFormulare } =
    useMutation({
      mutationFn: () =>
        customFetcher({
          endpoint: '/openformulare/pull',
          method: 'POST',
          body: {},
        }) as Promise<IOpenFormularePullResult>,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      },
    });

  return { pullOpenFormulare, isPullingOpenFormulare };
};

export const useReopenDialogInbox = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: reopenDialogInbox, isPending: isReopening } =
    useMutation({
      mutationFn: (idDialogInbox: number) =>
        customFetcher({
          endpoint: `/dialoginbox/${idDialogInbox}/reopen`,
          method: 'POST',
        }),
      onSuccess: (_data, idDialogInbox) => {
        queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
        // Detail-Query nutzt [QUERY_KEY, idDialogInbox] — auch neu laden,
        // damit der UI-Status sofort von "ignored" auf "pending" springt.
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEY, idDialogInbox],
        });
      },
    });

  return { reopenDialogInbox, isReopening };
};

export const useIgnoreDialogInbox = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: ignoreDialogInbox, isPending: isIgnoring } = useMutation(
    {
      mutationFn: (idDialogInbox: number) =>
        customFetcher({
          endpoint: `/dialoginbox/${idDialogInbox}/ignore`,
          method: 'POST',
        }),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      },
    }
  );

  return { ignoreDialogInbox, isIgnoring };
};
